import { useCallback, useMemo, useRef, useState } from 'react';
import { Badge, Box, Button, Flex, Grid, Text } from '@chakra-ui/react';
import { PlanItem, PlanSection } from '@/lib/types';
import { PlanDraft } from '@/pages/planner/draft';
import { BuilderCtx, PlannerAction } from '@/pages/planner/planner-machine';
import api from '@/utils/api';
import usePlannerPlansStore from '@/utils/use-planner-plans';
import useCurriculumsStore from '@/utils/use-curriculums';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { useModalValueToggle } from '@/lib/hooks/use-modal-toggle';
import PlannerItemDrawer from '@/pages/planner/item-drawer/planner-item-drawer';
import PlanInfoForm, { PlanInfoFormHandle, PlanInfoFormValues } from './plan-info-form';
import PlanTimeTracker from './plan-time-tracker';
import SaveLibraryModal from './save-library-modal';
import SectionList from './section-list';

interface BuilderScreenProps {
  ctx: BuilderCtx;
  initialDraft: PlanDraft;
  dispatch: (action: PlannerAction) => void;
}

// New custom items are those with a library label chosen but no linked
// content yet — the set the save-to-library heads-up lists and
// materializeCustomItems creates.
function collectNewItems(sections: PlanSection[]): PlanItem[] {
  return sections.flatMap((section) =>
    section.items.filter((item) => item.content_id == null && item.content_type_id != null),
  );
}

// Custom items (content_type_id set by the required library select, no
// content_id yet) are materialized into real draft contents at save time —
// POST /contents, then linked into the plan via content_id/reference_id — so
// the coach can flesh them out later from that library. Runs before the plan
// save so the stored sections snapshot already points at the created
// contents. The content_type_id guard is defense-in-depth: handleSave blocks
// label-less items before this runs.
async function materializeCustomItems(
  sections: PlanSection[],
  workspaceId: number,
): Promise<{ sections: PlanSection[]; createdCount: number }> {
  let createdCount = 0;
  const materialized = await Promise.all(
    sections.map(async (section) => ({
      ...section,
      items: await Promise.all(
        section.items.map(async (item): Promise<PlanItem> => {
          if (item.content_id != null || item.content_type_id == null) return item;
          const res = await api.post('/contents', {
            status: 'draft',
            workspace_id: workspaceId,
            content_type_id: item.content_type_id,
            name: item.name,
            description: '',
            is_public: false,
            content: {},
          });
          createdCount += 1;
          return {
            ...item,
            content_id: res.data.data.id,
            content_reference_id: res.data.data.reference_id,
          };
        }),
      ),
    })),
  );
  return { sections: materialized, createdCount };
}

// "builder" — two-column plan builder. Left: plan info (RHF+zod, see
// plan-info-form.tsx). Right: sections (useState array, see section-list.tsx).
// Curriculum mode (opened from curBuild's "Build plan"/"Edit" or curSlotView's
// "Edit plan") shows a "Plan N of M" badge and saves via saveSlot, refreshing
// the parent curriculum afterward so Built/Empty chips update immediately.
export default function BuilderScreen({ ctx, initialDraft, dispatch }: BuilderScreenProps) {
  const [draft, setDraft] = useState<PlanDraft>(initialDraft);
  const [isSaving, setIsSaving] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showItemErrors, setShowItemErrors] = useState(false);
  // New custom items awaiting the save-to-library confirmation; null = closed.
  const [confirmItems, setConfirmItems] = useState<PlanItem[] | null>(null);
  const savePlan = usePlannerPlansStore((s) => s.savePlan);
  const saveSlot = useCurriculumsStore((s) => s.saveSlot);
  const fetchOneCurriculum = useCurriculumsStore((s) => s.fetchOne);
  const initialRef = useRef(initialDraft);
  const planInfoFormRef = useRef<PlanInfoFormHandle>(null);
  const itemDrawer = useModalValueToggle<PlanItem>();

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initialRef.current),
    [draft],
  );

  const isCurriculumMode = ctx.kind === 'curriculumPlan';

  const scheduledMinutes = useMemo(
    () => draft.sections.reduce((total, section) => total + (section.duration_minutes || 0), 0),
    [draft.sections],
  );

  const handleInfoChange = useCallback((values: PlanInfoFormValues) => {
    setDraft((prev) => ({ ...prev, ...values }));
  }, []);

  const handleSectionsChange = (sections: PlanSection[]) => {
    setDraft((prev) => ({ ...prev, sections }));
  };

  // Discard target: back to the plan's read-only view if one already exists
  // (editing), otherwise back to the date's choice screen (never-saved new
  // plan, so there's nothing to view yet). Curriculum-mode discards go back
  // to wherever the builder was opened from — curBuild by default, or
  // curSlotView when opened via that screen's "Edit plan" action.
  const fallbackScreen = ctx.kind === 'dayPlan'
    ? draft.id
      ? ({ name: 'view', date: ctx.date, scope: draft.scope } as const)
      : ({ name: 'choice', date: ctx.date, scope: draft.scope } as const)
    : ctx.returnTo === 'curSlotView'
      ? ({ name: 'curSlotView', curriculumId: ctx.curriculumId, slotId: ctx.slotId } as const)
      : ({ name: 'curBuild', curriculumId: ctx.curriculumId } as const);

  const handleDiscard = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    dispatch({ type: 'DISCARD_BUILDER', fallback: fallbackScreen });
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    dispatch({ type: 'DISCARD_BUILDER', fallback: fallbackScreen });
  };

  const handleSave = async () => {
    const isPlanInfoValid = await planInfoFormRef.current?.validate();
    if (!isPlanInfoValid) {
      toaster.create({
        type: 'error',
        title: 'Fix the highlighted fields before saving',
      });
      return;
    }
    // The library label is required on custom items — every one must name the
    // library its draft content is created in (covers legacy label-less items
    // on older plans too).
    const hasUnlabeledItems = draft.sections.some((section) =>
      section.items.some((item) => item.content_id == null && item.content_type_id == null),
    );
    if (hasUnlabeledItems) {
      setShowItemErrors(true);
      toaster.create({
        type: 'error',
        title: 'Choose a label for every custom item before saving',
      });
      return;
    }
    setShowItemErrors(false);
    // New custom items become draft library contents on save — surface that
    // via the heads-up dialog before proceeding, so it never happens silently.
    const newItems = collectNewItems(draft.sections);
    if (newItems.length > 0) {
      setConfirmItems(newItems);
      return;
    }
    await performSave();
  };

  const handleConfirmSave = async () => {
    await performSave();
    setConfirmItems(null);
  };

  // The save proper — runs after all validation (and the library heads-up,
  // when the plan has new custom items).
  const performSave = async () => {
    setIsSaving(true);
    try {
      const { sections, createdCount } = await materializeCustomItems(
        draft.sections,
        draft.workspace_id,
      );
      if (createdCount > 0) {
        // Sync the draft immediately so a failed plan save + retry doesn't
        // re-create the same library contents a second time.
        setDraft((prev) => ({ ...prev, sections }));
        toaster.create({
          type: 'success',
          title: `${createdCount} draft item${createdCount === 1 ? '' : 's'} added to your content library`,
        });
      }
      if (ctx.kind === 'curriculumPlan') {
        // Slots carry no learning_outcomes/prerequisites — those live on the
        // parent curriculum (edited via curBuild's "Edit details"), and the
        // draft only holds them so the builder can show them read-only.
        await saveSlot(ctx.slotId, {
          name: draft.name,
          duration_minutes: draft.duration_minutes,
          goals: draft.goals,
          notes: draft.notes,
          sections,
        });
        // Refresh the parent curriculum so curBuild's Built/Empty chips and
        // "x of y plans built" progress reflect this save immediately.
        await fetchOneCurriculum(ctx.curriculumId);
        toaster.create({ type: 'success', title: 'Curriculum plan saved' });
        dispatch({
          type: 'SAVED_CURRICULUM_PLAN',
          curriculumId: ctx.curriculumId,
          returnTo: ctx.returnTo,
          slotId: ctx.slotId,
        });
        return;
      }
      const saved = await savePlan({ ...draft, sections });
      toaster.create({ type: 'success', title: 'Practice plan saved' });
      dispatch({ type: 'SAVED_DAY_PLAN', date: saved.plan_date, scope: saved.scope });
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: error.response?.data?.error ?? 'Failed to save practice plan',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Flex
        align='center'
        justify='space-between'
        bg='brand.subtle'
        borderRadius='md'
        px={4}
        py={2}
        mb={4}
      >
        <Text fontSize='sm' fontWeight='600' color='brand.solid'>
          {isCurriculumMode
            ? `Editing curriculum plan — ${ctx.curriculumName}`
            : 'Editing plan'}
          {isDirty && ' — unsaved changes'}
        </Text>
        <Flex gap={2}>
          {/* Which of the date's slots this plan saves into — set before the
              builder opened (scope tab strip / choice screen) and immutable
              here. */}
          {!isCurriculumMode && (
            <Badge variant='outline' size='sm'>
              {draft.scope === 'personal' ? 'My plan' : 'Workspace plan'}
            </Badge>
          )}
          {isCurriculumMode && (
            <Badge variant='outline' size='sm'>
              Plan {ctx.sortOrder} of {ctx.planCount}
            </Badge>
          )}
          {draft.from_curriculum && (
            <Badge colorPalette='brand' size='sm'>
              From curriculum
            </Badge>
          )}
        </Flex>
      </Flex>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1.4fr' }} gap={6}>
        <Box>
          <Text textStyle='eyebrow' mb={3}>
            1. Plan information
          </Text>
          <PlanInfoForm
            draft={draft}
            isCurriculumMode={isCurriculumMode}
            onChange={handleInfoChange}
            formRef={planInfoFormRef}
          />
        </Box>

        <Box>
          <PlanTimeTracker scheduledMinutes={scheduledMinutes} planMinutes={draft.duration_minutes} />
          <SectionList
            sections={draft.sections}
            onChange={handleSectionsChange}
            onViewItem={itemDrawer.open}
            showItemErrors={showItemErrors}
          />
        </Box>
      </Grid>

      <Flex justify='flex-end' gap={3} mt={6}>
        <Button variant='ghost' onClick={handleDiscard} disabled={isSaving}>
          Discard
        </Button>
        <Button colorPalette='brand' onClick={handleSave} loading={isSaving}>
          {isCurriculumMode ? 'Save to curriculum' : 'Save plan'}
        </Button>
      </Flex>

      <DestructiveModal
        isOpen={showDiscardConfirm}
        onCancel={() => setShowDiscardConfirm(false)}
        onConfirm={confirmDiscard}
        title='Discard unsaved changes?'
        confirmText='Discard'
      >
        <Text fontSize='sm' color='fg.muted'>
          You have unsaved changes to this plan. Discarding will lose them.
        </Text>
      </DestructiveModal>

      <SaveLibraryModal
        isOpen={confirmItems != null}
        items={confirmItems ?? []}
        isSaving={isSaving}
        confirmText={isCurriculumMode ? 'Save to curriculum' : 'Save plan'}
        onCancel={() => {
          if (!isSaving) setConfirmItems(null);
        }}
        onConfirm={handleConfirmSave}
      />

      <PlannerItemDrawer isOpen={itemDrawer.isOpen} onClose={itemDrawer.close} item={itemDrawer.state} />
    </Box>
  );
}
